import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import ChatMessage from "./ChatMessage";
import HistoricTargetHeader from "./HistoricTargetHeader";
import Sidebar from "../layout/Sidebar";
import MobileSidebar from "../layout/MobileSidebar";
import { ScrollArea } from "@/components/ui/scroll-area";
import Footer from "../layout/Footer";
import { Message, ProcessedFile } from "@/context/types";
import PredictionResultCard from "./PredictionResultCard";
import { formatTargetCode, contentContainsTargetCode } from '@/utils/targetCodeUtils';
import { Share, Facebook, Twitter, Hash, MessageSquare, Link } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

interface HistoricTargetViewProps {
  targetId: string;
  targetType: 'location' | 'image' | 'prediction' | null;
  returnPage: number;
}

// Check if this is a welcome message
const isWelcomeMessage = (content: string): boolean => {
  return content.includes('Welcome to RemoteViz!') || 
         content.includes('Your journey into remote viewing starts here') ||
         content.includes('New Chat Started');
};

// Check if this is an internal analysis prompt (not the actual feedback)
const isInternalAnalysisPrompt = (content: string): boolean => {
  const internalPatterns = [
    /^TARGET:\s*(Type:|https?:)/,
    /^USER_IMPRESSIONS:/,
    /^ANALYSIS_REQUEST:/,
    /CRITICAL INSTRUCTION: Use ONLY second person language/,
    /VISION_ANALYSIS_REQUEST/,
    /IMAGE_COUNT:/,
    /Examples of CORRECT language:/,
    /^Analyze remote viewing session for Target/,
    /^Target [A-Z0-9-]+ revealed\.\s*$/
  ];
  return internalPatterns.some(pattern => pattern.test(content));
};

// Check if message is a target generation request
const isTargetGenerationRequest = (content: string): boolean => {
  const lower = content.toLowerCase().trim();
  return lower === 'location target' || 
         lower === 'image target' || 
         lower === 'prediction target' ||
         lower.includes('generate location target') ||
         lower.includes('generate image target') ||
         lower.includes('generate prediction target');
};

// Check if this is a reveal request for a specific target
const isRevealRequest = (content: string, targetCode: string): boolean => {
  const lower = content.toLowerCase();
  return lower.includes('reveal') && contentContainsTargetCode(content, targetCode);
};

// Check if message is target confirmation
const isTargetConfirmation = (content: string, targetCode: string): boolean => {
  return content.includes('Target Code:') && contentContainsTargetCode(content, targetCode);
};

// Check if message contains revealed content
const isRevealedContent = (content: string): boolean => {
  return content.includes('IMAGE_URL:') ||
         content.includes('MAP_URL:') ||
         content.includes('PREDICTION_TARGET:') ||
         content.includes('Here\'s your image target:') ||
         content.includes('Here\'s your location target:');
};

// Check if message is feedback analysis JSON or formatted feedback
const isFeedbackAnalysis = (content: string): boolean => {
  // JSON format
  if (content.trim().startsWith('{') && content.includes('"type": "feedback_analysis"')) {
    return true;
  }
  // Formatted markdown feedback
  const feedbackMarkers = [
    '## 📊 Impressions Overview',
    '## ✅ Accurate Impressions',
    '## ❌ Inaccurate Impressions',
    '## 🔄 Partial Matches',
    '## 💡 Insights',
    '**Accurate Elements**',
    '**Inaccurate Elements**',
    '**Target Details**',
    '**Analysis of User Notes**'
  ];
  return feedbackMarkers.some(marker => content.includes(marker));
};

// Check if message is prediction result
const isPredictionResult = (content: string): boolean => {
  return content.includes('PREDICTION_RESULT:');
};

// Check if message is impression confirmation
const isImpressionConfirmation = (content: string): boolean => {
  return content.includes('Your impression has been recorded!') ||
         content.includes('Image received!') ||
         content.includes('impressions have been recorded');
};

const HistoricTargetView: React.FC<HistoricTargetViewProps> = ({
  targetId,
  targetType,
  returnPage
}) => {
  const [loading, setLoading] = useState(true);
  const [predictionData, setPredictionData] = useState<{
    target: any;
    userPrediction: 'YES' | 'NO';
  } | null>(null);
  const [targetCode, setTargetCode] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Share functionality for mobile header
  const formattedCode = useMemo(() => formatTargetCode(targetCode), [targetCode]);
  const shareUrl = useMemo(() => window.location.href, []);

  const copyUrl = useCallback(async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success('URL copied to clipboard');
  }, [shareUrl]);

  const shareToFacebook = useCallback(() => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  }, [shareUrl]);

  const shareToX = useCallback(() => {
    const text = encodeURIComponent(`Check out my RemoteViz session: Target ${formattedCode} #RemoteViewing`);
    const url = `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  }, [formattedCode, shareUrl]);

  const shareToReddit = useCallback(() => {
    const title = encodeURIComponent(`My RemoteViz Session: Target ${formattedCode}`);
    const url = `https://reddit.com/submit?title=${title}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank');
  }, [formattedCode, shareUrl]);

  const shareToDiscord = useCallback(() => {
    navigator.clipboard.writeText(`Check out my RemoteViz session: Target ${formattedCode}\n${shareUrl}`);
    toast.success('Copied to clipboard - paste in Discord');
  }, [formattedCode, shareUrl]);

  useEffect(() => {
    const loadHistoricTarget = async () => {
      if (!user || !targetId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        let target: any = null;
        let sessionId = '';
        let code = '';
        let targetCreatedAt: string | null = null;
        let storedPrediction: string | null = null;
        let storedIsCorrect: boolean | null = null;

        // Fetch target based on type
        if (targetType === 'prediction') {
          // Fetch prediction target WITHOUT embedded user_predictions (avoids FK/embedding issues)
          const { data, error: fetchError } = await supabase
            .from('prediction_targets')
            .select(`
              id,
              target_code,
              session_id,
              created_at,
              yes_image_url,
              no_image_url,
              revealed_at,
              event_id,
              prediction_events (
                title,
                description,
                yes_outcome_label,
                no_outcome_label,
                close_time,
                kalshi_event_id,
                resolved_outcome,
                metadata
              )
            `)
            .eq('id', targetId)
            .eq('user_id', user.id)
            .maybeSingle();

          if (fetchError) throw fetchError;
          target = data;
          sessionId = data?.session_id || '';
          code = data?.target_code || '';
          targetCreatedAt = data?.created_at || null;

          // EXPLICIT QUERY: Fetch user prediction directly from user_predictions table
          if (data?.id) {
            const { data: predictionData, error: predError } = await supabase
              .from('user_predictions')
              .select('user_selected_outcome, is_correct')
              .eq('target_id', data.id)
              .eq('user_id', user.id)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            
            if (!predError && predictionData) {
              storedPrediction = predictionData.user_selected_outcome;
              storedIsCorrect = predictionData.is_correct;
              if (import.meta.env.DEV) {
                console.log('✅ HistoricTargetView: Found user prediction in database:', storedPrediction, 'is_correct:', storedIsCorrect);
              }
            } else if (import.meta.env.DEV) {
              console.log('⚠️ HistoricTargetView: No user prediction found in database, will try transcript inference');
            }
          }
        } else {
          const { data, error: fetchError } = await supabase
            .from('targets')
            .select('id, target_code, session_id, target_type, target_url, created_at, revealed_at, unsplash_data')
            .eq('id', targetId)
            .eq('user_id', user.id)
            .maybeSingle();

          if (fetchError) throw fetchError;
          target = data;
          sessionId = data?.session_id || '';
          code = data?.target_code || '';
          targetCreatedAt = data?.created_at || null;
        }

        if (!target || !sessionId || !code || !targetCreatedAt) {
          setError('Target not found');
          setLoading(false);
          return;
        }

        setTargetCode(code);

        // Determine time window for this target within the session.
        // Start = target.created_at (with a small negative buffer to avoid timestamp precision edge-cases)
        // End = next target (regular or prediction) created_at within the same session (if any)
        const startMs = new Date(targetCreatedAt).getTime() - 1000; // 1s buffer
        const startIso = new Date(startMs).toISOString();

        // Find next targets to determine end of window
        // IMPORTANT: Exclude current target by ID to avoid microsecond precision issues
        // (toISOString truncates microseconds, causing current target to match its own "gt" query)
        const [nextRegular, nextPrediction] = await Promise.all([
          supabase
            .from('targets')
            .select('created_at, target_code')
            .eq('session_id', sessionId)
            .eq('user_id', user.id)
            .gt('created_at', new Date(targetCreatedAt).toISOString())
            .neq('id', targetId)
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle(),
          supabase
            .from('prediction_targets')
            .select('created_at, target_code')
            .eq('session_id', sessionId)
            .eq('user_id', user.id)
            .gt('created_at', new Date(targetCreatedAt).toISOString())
            .neq('id', targetId)
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle(),
        ]);

        if (nextRegular.error) throw nextRegular.error;
        if (nextPrediction.error) throw nextPrediction.error;

        const endCandidates = [
          nextRegular.data?.created_at ? new Date(nextRegular.data.created_at).getTime() : null,
          nextPrediction.data?.created_at ? new Date(nextPrediction.data.created_at).getTime() : null,
        ].filter((t): t is number => typeof t === 'number');

        const endMs = endCandidates.length ? Math.min(...endCandidates) : null;
        // Guard: if end resolves to <= start (can happen with precision/rounding weirdness), treat as open-ended.
        const endIso = endMs && endMs > startMs ? new Date(endMs).toISOString() : null;

        // Fetch messages in the target window
        let messagesQuery = supabase
          .from('messages')
          .select('*')
          .eq('session_id', sessionId)
          .gte('created_at', startIso)
          .order('created_at', { ascending: true });

        if (endIso) {
          messagesQuery = messagesQuery.lt('created_at', endIso);
        }

        const { data: messagesData, error: messagesError } = await messagesQuery;
        if (messagesError) throw messagesError;

        const windowMessages = messagesData || [];

        const inferPredictionFromTranscript = (): string | null => {
          // We store the selection as a user message in live sessions:
          //   NEW FORMAT: "My prediction selection for XXXX-XXXX: **Option A**"
          //   OLD FORMAT: "My prediction selection: **Option A**" / "My prediction selection: **Option B**"
          // Older copies may also use "Your Selection" phrasing.
          
          // First pass: prefer messages that mention the current target code (new format)
          for (const m of windowMessages) {
            const raw = (m.content || '').toString();
            const c = raw.toLowerCase();

            const mentionsTargetCode = contentContainsTargetCode(raw, code);
            const looksLikeSelection =
              c.includes('my prediction selection') ||
              c.includes('your selection') ||
              c.includes('prediction selection');

            if (!looksLikeSelection) continue;
            
            // Prefer messages that explicitly reference this target code
            if (mentionsTargetCode || windowMessages.length === 1) {
              // Option A => YES, Option B => NO
              if (/(option\s*a\b)/i.test(raw)) return 'YES';
              if (/(option\s*b\b)/i.test(raw)) return 'NO';
            }
          }
          
          // Second pass: fall back to any selection message in window (legacy format)
          for (const m of windowMessages) {
            const raw = (m.content || '').toString();
            const c = raw.toLowerCase();

            const looksLikeSelection =
              c.includes('my prediction selection') ||
              c.includes('your selection') ||
              c.includes('prediction selection');

            if (!looksLikeSelection) continue;

            // Option A => YES, Option B => NO
            if (/(option\s*a\b)/i.test(raw)) return 'YES';
            if (/(option\s*b\b)/i.test(raw)) return 'NO';
          }

          // Last-resort: some messages might explicitly say YES/NO
          for (const m of windowMessages) {
            const c = (m.content || '').toLowerCase();
            if (c.includes('my prediction') && c.includes('yes')) return 'YES';
            if (c.includes('my prediction') && c.includes('no')) return 'NO';
          }

          return null;
        };

        if (!storedPrediction && targetType === 'prediction') {
          storedPrediction = inferPredictionFromTranscript();
        }

        const filteredMessages: Message[] = [];

        for (const msg of windowMessages) {
          const content = msg.content || '';
          const hasFileUrls = Array.isArray((msg as any).file_urls) && (msg as any).file_urls.length > 0;

          // Keep behavior consistent with live chat: don't show internal system prompts or the generic welcome.
          if (isWelcomeMessage(content)) continue;
          if (isInternalAnalysisPrompt(content)) continue;
          
          if (!content.trim() && !hasFileUrls) continue;

          // For prediction targets, inject persisted selection/correctness into the payload so UI can render consistently.
          let processedContent = content;
          if (targetType === 'prediction') {
            // Inject into reveal payload
            if (storedPrediction && content.includes('PREDICTION_TARGET:')) {
              try {
                const match = content.match(/PREDICTION_TARGET:(.+)/s);
                if (match) {
                  const parsedPrediction = JSON.parse(match[1]);
                  parsedPrediction.storedUserPrediction = storedPrediction;
                  if (typeof storedIsCorrect === 'boolean') {
                    parsedPrediction.resultStatus = storedIsCorrect ? 'correct' : 'incorrect';
                  }
                  processedContent = `PREDICTION_TARGET:${JSON.stringify(parsedPrediction)}`;
                }
              } catch {
                // ignore
              }
            }

            // Inject into persisted result payload (older sessions may have this message already)
            if (typeof storedIsCorrect === 'boolean' && content.includes('PREDICTION_RESULT:')) {
              try {
                const match = content.match(/PREDICTION_RESULT:(.+)/s);
                if (match) {
                  const parsedResult = JSON.parse(match[1]);
                  if (parsedResult?.predictionTarget) {
                    parsedResult.predictionTarget.resultStatus = storedIsCorrect ? 'correct' : 'incorrect';
                    processedContent = `PREDICTION_RESULT:${JSON.stringify(parsedResult)}`;
                  }
                }
              } catch {
                // ignore
              }
            }
          }

          addMessage(filteredMessages, { ...msg, content: processedContent });
        }

        // For prediction targets with a user selection, store data for the PredictionResultCard
        if (targetType === 'prediction' && storedPrediction && target) {
          const predictionEvent = target.prediction_events;
          setPredictionData({
            target: {
              id: target.id,
              target_code: target.target_code,
              yes_image_url: target.yes_image_url,
              no_image_url: target.no_image_url,
              event: predictionEvent ? {
                id: target.event_id,
                title: predictionEvent.title,
                description: predictionEvent.description,
                close_time: predictionEvent.close_time,
                yes_outcome_label: predictionEvent.yes_outcome_label,
                no_outcome_label: predictionEvent.no_outcome_label,
                kalshi_event_id: predictionEvent.kalshi_event_id,
                resolved_outcome: predictionEvent.resolved_outcome,
                metadata: predictionEvent.metadata,
              } : undefined,
            },
            userPrediction: storedPrediction.toUpperCase() as 'YES' | 'NO',
          });
        } else {
          setPredictionData(null);
        }

        if (import.meta.env.DEV) {
          console.log('📜 HistoricTargetView: Loaded window', {
            targetType,
            code: formatTargetCode(code),
            startIso,
            endIso,
            sessionId,
            totalInWindow: windowMessages.length,
            rendered: filteredMessages.length,
            storedPrediction,
          });
        }

        setMessages(filteredMessages);
      } catch (err) {
        console.error('❌ HistoricTargetView: Error loading target:', err);
        setError('Failed to load target history');
      } finally {
        setLoading(false);
      }
    };

    // Helper to add a message to the filtered list
    const addMessage = (list: Message[], msg: any) => {
      let processedFiles: ProcessedFile[] = [];
      if (msg.file_urls && msg.file_urls.length > 0) {
        processedFiles = msg.file_urls
          .filter((url: string) => url && url.startsWith('http'))
          .map((url: string, index: number) => ({
            type: 'image' as const,
            content: url,
            filename: url.split('/').pop() || `image-${index + 1}.jpg`,
          }));
      }

      list.push({
        id: msg.id,
        content: msg.content,
        type: msg.role === 'user' ? 'user' : 'system',
        timestamp: new Date(msg.created_at).getTime(),
        files: processedFiles.length > 0 ? processedFiles : undefined,
      });
    };

    loadHistoricTarget();
  }, [targetId, targetType, user]);

  // Scroll to bottom when messages load
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  if (loading) {
    return (
      <div className="flex w-full h-screen bg-background">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Spinner className="h-8 w-8 text-primary" />
          <p className="ml-2">Loading target history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex w-full h-screen bg-background">
        <Sidebar />
        <div className="flex-1 flex flex-col">
          <HistoricTargetHeader targetCode="Unknown" returnPage={returnPage} />
          <div className="flex-1 flex items-center justify-center">
            <p className="text-destructive">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full h-screen bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col relative min-w-0">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between p-4 border-b border-border bg-background">
          <MobileSidebar />
          <div className="text-foreground text-lg font-medium">
            {formattedCode}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-muted">
                <Share className="h-5 w-5 text-muted-foreground" />
                <span className="sr-only">Share session</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={copyUrl} className="cursor-pointer">
                <Link className="mr-2 h-4 w-4" />
                Copy URL
              </DropdownMenuItem>
              <DropdownMenuItem onClick={shareToFacebook} className="cursor-pointer">
                <Facebook className="mr-2 h-4 w-4" />
                Facebook
              </DropdownMenuItem>
              <DropdownMenuItem onClick={shareToX} className="cursor-pointer">
                <Twitter className="mr-2 h-4 w-4" />
                X (Twitter)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={shareToReddit} className="cursor-pointer">
                <Hash className="mr-2 h-4 w-4" />
                Reddit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={shareToDiscord} className="cursor-pointer">
                <MessageSquare className="mr-2 h-4 w-4" />
                Discord
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:block">
          <HistoricTargetHeader targetCode={targetCode} returnPage={returnPage} />
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 px-2 sm:px-4 md:px-8 lg:px-16 xl:px-24 py-6">
          <div className="flex flex-col gap-6 max-w-4xl mx-auto w-full">
            {messages.length === 0 && !predictionData ? (
              <div className="text-center py-16 text-muted-foreground space-y-2">
                <p className="text-lg">📭 No impressions were recorded for this target.</p>
                <p className="text-sm">It looks like this session was started but no impressions were submitted.</p>
              </div>
            ) : (
              <>
                {messages.map((message, index) => (
                  <ChatMessage
                    key={message.id || index}
                    content={message.content}
                    type={message.type}
                    timestamp={message.timestamp}
                    files={message.files}
                    targetId={targetId}
                    targetType={targetType ?? undefined}
                  />
                ))}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Read-only notice */}
        <div className="px-2 sm:px-4 md:px-8 lg:px-16 xl:px-24 py-4 border-t border-border bg-muted/30">
          <div className="max-w-4xl mx-auto w-full text-center text-sm text-muted-foreground">
            This is a read-only view of a historic session.
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default HistoricTargetView;